import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Plus, Building, Trash2, Loader2, Camera, MapPin, Download, Upload } from "lucide-react";
import { NoApiKeyBanner } from "@/components/NoApiKeyBanner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { LocalStorageManager } from "@/lib/storage";
import type { Project } from "@shared/schema";
import { projectExportSchema } from "@shared/schema";

export function MobileDashboard() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null);
  const [exportingId, setExportingId] = useState<number | null>(null);
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef<HTMLInputElement>(null);
  const importAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => { importAbortRef.current?.abort(); };
  }, []);

  const { data: allProjects, isLoading } = useQuery<Project[]>({
    queryKey: ["/api/projects"],
  });

  const projects = useMemo(
    () => (allProjects ?? []).filter((p) => p.status !== "completed"),
    [allProjects],
  );

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/projects/${id}`),
    onSuccess: (_data, id) => {
      LocalStorageManager.deleteProject(String(id));
      queryClient.invalidateQueries({ queryKey: ["/api/projects"] });
      queryClient.invalidateQueries({ queryKey: ["/api/projects/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/projects/search"] });
      toast({
        title: "Projekt gelöscht",
        description: "Das Projekt wurde erfolgreich entfernt.",
      });
      setDeleteTarget(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Fehler beim Löschen",
        description: error.message || "Das Projekt konnte nicht gelöscht werden.",
        variant: "destructive",
      });
    },
  });

  const isDeleting = (id: number) =>
    deleteMutation.isPending && deleteTarget?.id === id;

  const handleExport = async (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    setExportingId(project.id);
    try {
      const res = await fetch(`/api/projects/${project.id}/export`, {
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { message?: string }).message || "Export fehlgeschlagen");
      }
      const json = await res.json();
      const blob = new Blob([JSON.stringify(json, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const date = new Date().toISOString().slice(0, 10);
      const safeName = project.name.replace(/[^a-zA-Z0-9äöüÄÖÜß_\-]/g, "_").slice(0, 50);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fassadencheck_${safeName}_${date}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({
        title: "Export fehlgeschlagen",
        description: err instanceof Error ? err.message : "Unbekannter Fehler",
        variant: "destructive",
      });
    } finally {
      setExportingId(null);
    }
  };

  const handleImportFile = async (file: File) => {
    if (!file.name.endsWith(".json")) {
      toast({ title: "Ungültige Datei", description: "Bitte eine .json-Exportdatei auswählen.", variant: "destructive" });
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast({ title: "Datei zu groß", description: "Die Exportdatei darf maximal 50 MB groß sein.", variant: "destructive" });
      return;
    }
    setImporting(true);
    toast({ title: "Projekt wird importiert …", description: "Bitte warten." });
    const abortCtrl = new AbortController();
    importAbortRef.current = abortCtrl;
    try {
      const text = await file.text();
      let parsed: unknown;
      try {
        parsed = JSON.parse(text);
      } catch {
        throw new Error("Die Datei enthält kein gültiges JSON.");
      }
      const validated = projectExportSchema.safeParse(parsed);
      if (!validated.success) {
        const firstIssue = validated.error.issues[0];
        if (firstIssue?.path?.includes("schemaVersion")) {
          throw new Error("Ungültiges Exportformat. Bitte eine gültige FassadenCheck-Exportdatei verwenden.");
        }
        throw new Error(`Ungültige Exportdatei: ${firstIssue?.message ?? "Unbekanntes Format"}`);
      }
      const res = await apiRequest("POST", "/api/projects/import", validated.data, abortCtrl.signal);
      const body = await res.json() as { projectId?: number };
      queryClient.invalidateQueries({ queryKey: ["/api/projects"] });
      queryClient.invalidateQueries({ queryKey: ["/api/projects/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/projects/search"] });
      toast({
        title: "Projekt importiert",
        description: `„${validated.data.project.name}" wurde erfolgreich importiert.`,
      });
      if (body.projectId) {
        navigate(`/project/edit/${body.projectId}`);
      }
    } catch (err) {
      if ((err as { name?: string }).name === "AbortError") return;
      toast({
        title: "Import fehlgeschlagen",
        description: err instanceof Error ? err.message : "Unbekannter Fehler",
        variant: "destructive",
      });
    } finally {
      setImporting(false);
      importAbortRef.current = null;
    }
  };

  return (
    <div className="px-4 py-5 pb-28 max-w-xl mx-auto">
      <input
        ref={importInputRef}
        type="file"
        accept=".json"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleImportFile(file);
          e.target.value = "";
        }}
      />

      <div className="mb-5">
        <h1 className="text-2xl font-bold text-bauwerk-teal mb-1">
          Vor-Ort-Modus
        </h1>
        <p className="text-sm text-gray-600">
          Schnell ein Objekt erfassen und Fotos aufnehmen.
        </p>
      </div>

      <NoApiKeyBanner />

      <Button
        type="button"
        onClick={() => navigate("/project/create")}
        className="w-full mb-3 bg-bauwerk-orange hover:bg-bauwerk-light-orange py-4 h-auto text-base shadow-md"
        data-testid="button-mobile-new-project"
      >
        <Plus className="w-5 h-5 mr-2" />
        Neues Projekt anlegen
      </Button>

      <Button
        type="button"
        variant="outline"
        disabled={importing}
        onClick={() => importInputRef.current?.click()}
        className="w-full mb-6 border-bauwerk-teal text-bauwerk-teal hover:bg-bauwerk-mint py-4 h-auto text-base"
      >
        {importing ? (
          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
        ) : (
          <Upload className="w-5 h-5 mr-2" />
        )}
        Projekt importieren
      </Button>

      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-semibold text-bauwerk-teal">
          Meine Projekte
        </h2>
        {projects && projects.length > 0 && (
          <span className="text-xs text-gray-500">
            {projects.length} {projects.length === 1 ? "Projekt" : "Projekte"}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4 space-y-2">
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
                <Skeleton className="h-3 w-1/3" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : !projects || projects.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center">
            <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Building className="w-7 h-7 text-gray-400" />
            </div>
            <p className="text-gray-600 text-sm mb-1">
              Noch keine Projekte vorhanden.
            </p>
            <p className="text-xs text-gray-500">
              Tippe oben auf „Neues Projekt anlegen", um zu starten.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {projects.map((project) => (
            <Card
              key={project.id}
              className="overflow-hidden active:bg-gray-50 transition-colors"
              data-testid={`mobile-project-card-${project.id}`}
            >
              <CardContent className="p-0">
                <button
                  type="button"
                  onClick={() => navigate(`/project/edit/${project.id}`)}
                  className="w-full text-left p-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-bauwerk-teal focus-visible:ring-inset"
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="font-semibold text-bauwerk-teal text-base leading-snug break-words">
                      {project.name}
                    </h3>
                    <Badge
                      variant="secondary"
                      className="bg-bauwerk-teal/10 text-bauwerk-teal whitespace-nowrap"
                    >
                      <Camera className="w-3 h-3 mr-1" />
                      Öffnen
                    </Badge>
                  </div>
                  {project.address && (
                    <p className="text-sm text-gray-600 flex items-start gap-1.5 mb-1">
                      <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gray-400" />
                      <span className="break-words">{project.address}</span>
                    </p>
                  )}
                  <p className="text-xs text-gray-500">
                    {project.createdAt
                      ? new Date(project.createdAt).toLocaleDateString("de-DE")
                      : ""}
                  </p>
                </button>
                <div className="border-t border-gray-100 px-4 py-2 flex justify-between items-center">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-bauwerk-teal hover:text-bauwerk-teal hover:bg-bauwerk-mint py-2.5 px-3 h-auto"
                    aria-label={`Projekt ${project.name} exportieren`}
                    disabled={exportingId === project.id}
                    onClick={(e) => handleExport(project, e)}
                    data-testid={`mobile-button-export-project-${project.id}`}
                  >
                    {exportingId === project.id ? (
                      <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-1.5" />
                    )}
                    Exportieren
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 py-2.5 px-3 h-auto"
                    aria-label={`Projekt ${project.name} löschen`}
                    disabled={isDeleting(project.id)}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(project);
                    }}
                    data-testid={`mobile-button-delete-project-${project.id}`}
                  >
                    {isDeleting(project.id) ? (
                      <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4 mr-1.5" />
                    )}
                    Löschen
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open && !deleteMutation.isPending) {
            setDeleteTarget(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Projekt löschen</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie das Projekt{" "}
              <span className="font-semibold">{deleteTarget?.name}</span>{" "}
              wirklich unwiderruflich löschen? Alle zugehörigen Fotos werden mit
              entfernt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (deleteTarget) deleteMutation.mutate(deleteTarget.id);
              }}
              disabled={deleteMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Lösche...
                </>
              ) : (
                "Löschen"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
